/**
 * Referral Cookie Utility
 *
 * Sets/gets/deletes the referral code cookie with Domain=.zapify.pro
 * so it's accessible across all subdomains (zapify.pro, ai.zapify.pro, dev.zapify.pro)
 */

const COOKIE_NAME = "zapify_referral";
const COOKIE_MAX_AGE = 30 * 24 * 60 * 60; // 30 days in seconds

/**
 * Get the cookie domain based on current hostname
 * - Production/Staging: .zapify.pro (works across subdomains)
 * - Development (localhost): undefined (browser default)
 */
function getCookieDomain(): string | undefined {
  if (typeof window === "undefined") return undefined;

  const hostname = window.location.hostname;

  // For localhost, don't set domain (browser handles it)
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return undefined;
  }

  // For zapify.pro and subdomains, use .zapify.pro
  if (hostname.endsWith("zapify.pro")) {
    return ".zapify.pro";
  }

  // For any other domain, don't set domain
  return undefined;
}

/**
 * Set the referral code cookie
 */
export function setReferralCookie(code: string): void {
  if (typeof document === "undefined") return;

  const domain = getCookieDomain();
  const secure = window.location.protocol === "https:";

  let cookieString = `${COOKIE_NAME}=${encodeURIComponent(code)}; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax`;

  if (domain) {
    cookieString += `; Domain=${domain}`;
  }

  if (secure) {
    cookieString += "; Secure";
  }

  document.cookie = cookieString;
}

/**
 * Get the referral code from cookie
 */
export function getReferralCookie(): string | null {
  if (typeof document === "undefined") return null;

  const cookies = document.cookie.split(";");

  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === COOKIE_NAME && value) {
      return decodeURIComponent(value);
    }
  }

  return null;
}

/**
 * Delete the referral code cookie
 */
export function deleteReferralCookie(): void {
  if (typeof document === "undefined") return;

  const domain = getCookieDomain();
  const secure = window.location.protocol === "https:";

  // Set cookie with expired date to delete it
  let cookieString = `${COOKIE_NAME}=; Path=/; Max-Age=0; SameSite=Lax`;

  if (domain) {
    cookieString += `; Domain=${domain}`;
  }

  if (secure) {
    cookieString += "; Secure";
  }

  document.cookie = cookieString;
}
